/**
 * The files this browser has brought to the platform, per user,
 * with the image each became, so files chosen again are neither anonymized nor sent again: after
 * an interrupted upload (UPL-04) or when the same folder is dropped twice (UPL-07). The platform
 * confirms the images still exist first, so a file whose image was deleted meanwhile, on any
 * device, is checked anew. Only a hash of each file's path, size and modification time is kept,
 * never its name, which may identify a patient. A changed file, such as one the standalone app
 * exported again, hashes differently and is sent.
 */
import { sha256 } from '@noble/hashes/sha2'
import { bytesToHex } from '@noble/hashes/utils'

import { readStored, writeStored } from '../lib/stored'
import type { Source } from './zip'

const KEPT = 20_000 // about 1.4 MB of browser storage; the oldest go first
const ASKED = 1_000 // image IDs per question to the platform
const utf8 = new TextEncoder()

const fingerprint = (file: Source) =>
  bytesToHex(sha256(utf8.encode(`${file.webkitRelativePath || file.name}\0${file.size}\0${file.lastModified}`))).slice(0, 32)

/** A file and the image it became on the platform: stored, or found there already. */
export type Sent = readonly [file: Source, imageId: string]

export class SentFiles {
  private readonly key: string
  private readonly images = new Map<string, string>() // file hash → image ID

  constructor(userId: string) {
    // Named as when uploads into projects were remembered apart, so the files sent before stay known.
    this.key = `pedfx.sent.${userId}.personal`
    for (const entry of readStored(this.key)?.split(' ') ?? []) {
      const [hash, imageId] = entry.split(':')
      if (hash && imageId) this.images.set(hash, imageId) // entries of the older form name no image
    }
  }

  imageOf(file: Source): string | undefined {
    return this.images.get(fingerprint(file))
  }

  add(sent: readonly Sent[]): void {
    if (sent.length === 0) return
    for (const [file, imageId] of sent) {
      const hash = fingerprint(file)
      this.images.delete(hash) // re-added as the newest
      this.images.set(hash, imageId)
    }
    for (const oldest of this.images.keys()) {
      if (this.images.size <= KEPT) break
      this.images.delete(oldest)
    }
    this.save()
  }

  /**
   * The files sent before whose images the platform still holds (`ask` answers which of the given
   * image IDs it holds). The files of the other images are forgotten.
   */
  async present(files: readonly Source[], ask: (imageIds: string[]) => Promise<string[]>): Promise<Set<Source>> {
    const filesOf = new Map<string, Source[]>()
    for (const file of files) {
      const imageId = this.imageOf(file)
      if (imageId) filesOf.set(imageId, [...(filesOf.get(imageId) ?? []), file])
    }
    const asked = [...filesOf.keys()]
    const held = new Set<string>()
    for (let start = 0; start < asked.length; start += ASKED) {
      for (const imageId of await ask(asked.slice(start, start + ASKED))) held.add(imageId)
    }
    const gone = asked.filter((imageId) => !held.has(imageId))
    if (gone.length) {
      for (const file of gone.flatMap((imageId) => filesOf.get(imageId)!)) this.images.delete(fingerprint(file))
      this.save()
    }
    return new Set([...held].flatMap((imageId) => filesOf.get(imageId) ?? []))
  }

  private save(): void {
    writeStored(this.key, [...this.images].map(([hash, imageId]) => `${hash}:${imageId}`).join(' '))
  }
}
